/** The numbers page's ranges. Apart from compute.ts so the loading shell can
 *  draw the chips without pulling the whole of it in. */

export type RangeKey = "day" | "week" | "month" | "all";

/** Where a range starts and ends. Null `from` is all time; null `to` is now. */
export interface Bounds {
  from: number | null;
  to: number | null;
}

/** When paying on the page went live for every customer (the first live
 *  checkout session is 28 September 2026, 00:09 UTC). Every "I want to order"
 *  since opened the payment form, so leads that pressed it from then on count
 *  as offered it even from before that was stamped on the lead. */
export const PAY_HERE_SINCE = Date.UTC(2026, 8, 28);

/** The chips. A day, week or month opens on the current one and steps back
 *  from there, so "last month" is Month and one step back rather than a chip
 *  of its own. */
export const RANGES: { key: RangeKey; label: string }[] = [
  { key: "day", label: "Day" },
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
  { key: "all", label: "All" },
];

export const DEFAULT_RANGE: RangeKey = "day";

/** One day, week or month: what it is called, and where it starts and ends. */
export interface Period {
  /** "Today", "Last week", "August 2026". */
  label: string;
  /** The dates under a label that does not say them, else null. */
  dates: string | null;
  bounds: Bounds;
}

const NAMES: Record<Exclude<RangeKey, "all">, [string, string]> = {
  day: ["Today", "Yesterday"],
  week: ["This week", "Last week"],
  month: ["This month", "Last month"],
};

/** The period `offset` steps back from the current one (0 is today, this week
 *  or this month; -1 the one before), in local time. Weeks start on Monday, as
 *  the chart's do. The current one runs up to now; any other ends where the
 *  next one starts. */
export function periodOf(range: RangeKey, offset: number, now: number): Period {
  if (range === "all") return { label: "All time", dates: null, bounds: { from: null, to: null } };
  const from = startOf(range, offset, now);
  const to = offset === 0 ? null : startOf(range, offset + 1, now);
  const dates = datesOf(range, from, now);
  const name = NAMES[range][-offset];
  return name ? { label: name, dates, bounds: { from, to } } : { label: dates, dates: null, bounds: { from, to } };
}

function startOf(range: Exclude<RangeKey, "all">, offset: number, now: number): number {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  if (range === "day") d.setDate(d.getDate() + offset);
  if (range === "week") d.setDate(d.getDate() - ((d.getDay() + 6) % 7) + 7 * offset);
  // To the 1st before moving, so the 31st never rolls over into the month after.
  if (range === "month") d.setMonth(d.getMonth() + offset, 1);
  return d.getTime();
}

/** "Fri, 10 Oct", "6 Oct – 12 Oct", "August 2026". The year only once it is
 *  not this one, except on a month, where it is short enough to keep. */
function datesOf(range: Exclude<RangeKey, "all">, from: number, now: number): string {
  const start = new Date(from);
  if (range === "month") return start.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const year = start.getFullYear() === new Date(now).getFullYear() ? undefined : ("numeric" as const);
  if (range === "day") {
    return start.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year });
  }
  const end = new Date(from);
  end.setDate(end.getDate() + 6);
  const short = (d: Date) => d.toLocaleDateString(undefined, { day: "numeric", month: "short", year });
  return `${short(start)} – ${short(end)}`;
}
